import { useEffect, useRef, useState } from 'react';

const features = [
  {
    icon: 'ri-lock-2-line',
    title: 'End-to-End Encryption',
    desc: 'Your data is encrypted using AES-256 at every layer, ensuring no one can intercept or read your information.',
  },
  {
    icon: 'ri-speed-mini-line',
    title: 'Blazing Performance',
    desc: 'Optimized for iOS with minimal resource footprint so your apps stay lightning fast even while running in the background.',
  },
  {
    icon: 'ri-eye-off-line',
    title: 'Zero-Log Privacy',
    desc: 'We never store, track, or share your activity. Your usage data belongs to you and no one else.',
  },
  {
    icon: 'ri-smartphone-line',
    title: 'Native iOS Design',
    desc: 'Built using Apple\'s Human Interface Guidelines for a seamless experience that feels completely at home on your device.',
  },
  {
    icon: 'ri-shield-flash-line',
    title: 'Threat Detection',
    desc: 'Real-time monitoring identifies and blocks suspicious activity, malicious sites, and unsafe connections instantly.',
  },
  {
    icon: 'ri-settings-4-line',
    title: 'Smart Automation',
    desc: 'Intelligent scheduling and automation keep your device optimized and protected without any manual effort required.',
  },
  {
    icon: 'ri-battery-charge-line',
    title: 'Battery Friendly',
    desc: 'Engineered to run efficiently in the background without draining your battery or slowing down other apps.',
  },
  {
    icon: 'ri-customer-service-2-line',
    title: 'Dedicated Support',
    desc: 'Our expert support team is always available to help you get the most out of every FOCUS product.',
  },
];

export default function FeaturesSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section id="features" className="py-32 bg-gray-50" ref={ref}>
      <div className="max-w-7xl mx-auto px-8">
        {/* Header */}
        <div
          className={`text-center mb-20 transition-all duration-700 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="inline-block border border-emerald-500 text-emerald-600 text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-5">
            Features
          </span>
          <h2 className="font-syne font-bold text-5xl text-gray-900 leading-tight mb-5 max-w-2xl mx-auto">
            Everything You Need,
            <br />
            <span className="text-emerald-500">Nothing You Don't</span>
          </h2>
          <p className="text-gray-500 text-lg max-w-xl mx-auto leading-relaxed">
            Each feature is purposefully designed to make your iOS experience safer, cleaner, and more enjoyable — every single day.
          </p>
        </div>

        {/* Feature Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {features.map((feat, i) => (
            <div
              key={i}
              className={`group p-7 rounded-2xl bg-white border border-gray-100 hover:border-emerald-400 hover:bg-emerald-500 transition-all duration-300 cursor-default ${
                visible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: visible ? `${i * 60}ms` : '0ms' }}
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-emerald-50 group-hover:bg-white/20 transition-colors duration-300 mb-5">
                <i className={`${feat.icon} text-emerald-500 group-hover:text-white text-2xl transition-colors duration-300`} />
              </div>
              <h3 className="font-syne font-semibold text-gray-900 group-hover:text-white text-base mb-3 transition-colors duration-300 leading-snug">
                {feat.title}
              </h3>
              <p className="text-gray-500 group-hover:text-white/80 text-sm leading-relaxed transition-colors duration-300">
                {feat.desc}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
